import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabaseClient';
import AppLayout from '../components/layout/AppLayout';
import type { ProductDetails, Strain } from '../types/strain';
import type { Brand } from '../types/brand';
import SearchableSelect from '../components/forms/SearchableSelect';
import {
    Plus,
    Tag,
    Sparkles,
    Edit3,
    Trash2,
    Package,
    Info,
    Dna,
    Zap
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

export default function Products() {
    const [products, setProducts] = useState<ProductDetails[]>([]);
    const [strains, setStrains] = useState<Strain[]>([]);
    const [brands, setBrands] = useState<Brand[]>([]);
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [editingId, setEditingId] = useState<string | null>(null);
    const navigate = useNavigate();

    // Form state
    const [strainId, setStrainId] = useState('');
    const [brandId, setBrandId] = useState('');
    const [productType, setProductType] = useState<'flower' | 'preroll' | 'vape' | 'edible'>('flower');
    const [thcPercent, setThcPercent] = useState<number | undefined>(undefined);
    const [cbdPercent, setCbdPercent] = useState<number | undefined>(undefined);
    const [labelName, setLabelName] = useState('');
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        fetchAll();
    }, []);

    const fetchAll = async () => {
        await Promise.all([fetchProducts(), fetchStrains(), fetchBrands()]);
        setLoading(false);
    };

    const fetchProducts = async () => {
        const { data, error } = await supabase
            .from('products')
            .select(`
        *,
        strain:strains(*),
        brand:brands(*)
      `)
            .order('created_at', { ascending: false });

        if (error) {
            console.error('Error fetching products:', error);
        } else if (data) {
            setProducts(data as ProductDetails[]);
        }
    };

    const fetchStrains = async () => {
        const { data } = await supabase.from('strains').select('*').order('name');
        if (data) setStrains(data);
    };

    const fetchBrands = async () => {
        const { data } = await supabase.from('brands').select('*').order('name');
        if (data) setBrands(data);
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setSaving(true);

        const productData = {
            strain_id: strainId || null,
            brand_id: brandId || null,
            product_type: productType,
            thc_percent: thcPercent || null,
            cbd_percent: cbdPercent || null,
            label_name: labelName || null,
        };

        if (editingId) {
            const { error } = await supabase
                .from('products')
                .update(productData)
                .eq('id', editingId);

            if (error) {
                console.error('Error updating product:', error);
                alert('Error updating product.');
            } else {
                resetForm();
                fetchProducts();
            }
        } else {
            const { error } = await supabase.from('products').insert(productData);

            if (error) {
                console.error('Error adding product:', error);
                alert('Error adding product.');
            } else {
                resetForm();
                fetchProducts();
            }
        }
        setSaving(false);
    };

    const handleEdit = (product: ProductDetails) => {
        setEditingId(product.id);
        setStrainId(product.strain_id || '');
        setBrandId(product.brand_id || '');
        setProductType(product.product_type);
        setThcPercent(product.thc_percent || undefined);
        setCbdPercent(product.cbd_percent || undefined);
        setLabelName(product.label_name || '');
        setShowForm(true);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleDelete = async (id: string) => {
        if (!confirm('Are you sure you want to delete this product? Sessions using this product will lose the link.')) {
            return;
        }

        const { error } = await supabase.from('products').delete().eq('id', id);

        if (error) {
            console.error('Error deleting product:', error);
            alert('Error deleting product.');
        } else {
            fetchProducts();
        }
    };

    const resetForm = () => {
        setStrainId('');
        setBrandId('');
        setProductType('flower');
        setThcPercent(undefined);
        setCbdPercent(undefined);
        setLabelName('');
        setShowForm(false);
        setEditingId(null);
    };

    const typeIcons: Record<string, any> = {
        flower: <Zap className="text-emerald-400" size={24} />,
        preroll: <Tag className="text-amber-400" size={24} />,
        vape: <Sparkles className="text-blue-400" size={24} />,
        edible: <Zap className="text-purple-400" size={24} />,
    };

    const typeLabels: Record<string, string> = {
        flower: 'Flower (Bud)',
        preroll: 'Pre-roll',
        vape: 'Vape Cart',
        edible: 'Edible',
    };

    if (loading) {
        return (
            <AppLayout title="Products">
                <div className="flex flex-col items-center justify-center min-h-[60vh]">
                    <Package className="text-accent-purple animate-pulse mb-4" size={48} />
                    <p className="text-dark-muted animate-pulse font-medium">Loading inventory...</p>
                </div>
            </AppLayout>
        );
    }

    return (
        <AppLayout title="Products">
            <div className="space-y-6">
                <div className="flex items-center justify-between">
                    <div>
                        <h2 className="text-2xl font-black tracking-tight uppercase">Product Ledger</h2>
                        <p className="text-[10px] text-dark-muted font-bold tracking-widest uppercase mt-1">Inventory & Cross-Links</p>
                    </div>
                    <button
                        onClick={() => {
                            if (showForm) resetForm();
                            else setShowForm(true);
                        }}
                        className={`flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-bold transition-all ${showForm ? 'bg-dark-surface border border-dark-border text-dark-muted' : 'bg-accent-purple text-white shadow-lg shadow-accent-purple/20 active:scale-95'
                            }`}
                    >
                        {showForm ? 'Cancel' : <><Plus size={14} /> New Product</>}
                    </button>
                </div>

                {/* Add/Edit Form */}
                {showForm && (
                    <form onSubmit={handleSubmit} className="card bg-dark-surface/50 border-accent-purple/20 space-y-6 animate-in slide-in-from-top-4 duration-300">
                        <div className="flex items-center gap-3">
                            <div className="p-2.5 bg-accent-purple/10 rounded-2xl border border-accent-purple/20">
                                <Package size={20} className="text-accent-purple" />
                            </div>
                            <div>
                                <h2 className="text-lg font-black tracking-tight">{editingId ? 'Modify Product' : 'Register New Product'}</h2>
                                <p className="text-[10px] text-dark-muted font-bold uppercase tracking-widest">Registry Association</p>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <SearchableSelect
                                label="Brand Association *"
                                placeholder="Select a brand..."
                                options={brands.map(b => ({
                                    id: b.id,
                                    label: b.name,
                                    subLabel: b.brand_type
                                }))}
                                value={brandId}
                                onChange={setBrandId}
                                addNewAction={{
                                    label: 'Add New Brand',
                                    onClick: () => navigate('/brands')
                                }}
                            />

                            <SearchableSelect
                                label="Strain Genetics *"
                                placeholder="Search library..."
                                options={strains.map(s => ({
                                    id: s.id,
                                    label: s.name,
                                    subLabel: s.strain_type
                                }))}
                                value={strainId}
                                onChange={setStrainId}
                                addNewAction={{
                                    label: 'Add New Strain',
                                    onClick: () => navigate('/strains')
                                }}
                            />
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                            <div className="space-y-1.5 md:col-span-1">
                                <label htmlFor="type" className="text-[10px] font-black text-dark-muted uppercase tracking-widest">Vessel / Medium</label>
                                <select
                                    id="type"
                                    value={productType}
                                    onChange={(e) => setProductType(e.target.value as any)}
                                    className="input h-12 text-sm font-bold bg-dark-bg appearance-none cursor-pointer"
                                >
                                    {Object.entries(typeLabels).map(([val, label]) => (
                                        <option key={val} value={val}>{label}</option>
                                    ))}
                                </select>
                            </div>

                            <div className="space-y-1.5">
                                <label htmlFor="thc" className="text-[10px] font-black text-dark-muted uppercase tracking-widest">Potency (THC %)</label>
                                <div className="relative">
                                    <Zap size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-accent-green/40" />
                                    <input
                                        id="thc"
                                        type="number"
                                        step="0.1"
                                        value={thcPercent || ''}
                                        onChange={(e) => setThcPercent(e.target.value ? Number(e.target.value) : undefined)}
                                        className="input h-12 text-sm font-bold bg-dark-bg pl-12"
                                        placeholder="0.0"
                                    />
                                </div>
                            </div>

                            <div className="space-y-1.5">
                                <label htmlFor="cbd" className="text-[10px] font-black text-dark-muted uppercase tracking-widest">Balance (CBD %)</label>
                                <div className="relative">
                                    <Zap size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-accent-blue/40" />
                                    <input
                                        id="cbd"
                                        type="number"
                                        step="0.1"
                                        value={cbdPercent || ''}
                                        onChange={(e) => setCbdPercent(e.target.value ? Number(e.target.value) : undefined)}
                                        className="input h-12 text-sm font-bold bg-dark-bg pl-12"
                                        placeholder="0.0"
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="space-y-1.5">
                            <label htmlFor="label" className="text-[10px] font-black text-dark-muted uppercase tracking-widest">Display / Label Name</label>
                            <input
                                id="label"
                                type="text"
                                value={labelName}
                                onChange={(e) => setLabelName(e.target.value)}
                                className="input h-12 text-sm font-bold bg-dark-bg"
                                placeholder="e.g. Reserve Glass Jar #4"
                            />
                        </div>

                        <button
                            type="submit"
                            disabled={saving}
                            className="btn-primary w-full py-4 text-sm font-black uppercase tracking-widest shadow-xl shadow-accent-purple/20 active:scale-95 transition-all"
                        >
                            {saving ? 'Processing...' : (editingId ? 'Commit Changes' : 'Initialize Product')}
                        </button>
                    </form>
                )}

                {/* Product List */}
                {products.length === 0 ? (
                    <div className="card text-center py-24 bg-dark-surface/20 border-dashed border-dark-border group cursor-pointer hover:border-accent-purple/30 transition-all" onClick={() => setShowForm(true)}>
                        <div className="inline-flex p-6 rounded-3xl bg-dark-bg border border-dark-border mb-6 group-hover:scale-110 transition-transform">
                            <Package size={48} className="text-accent-purple/30" />
                        </div>
                        <h2 className="text-xl font-bold mb-2">No Associations Yet</h2>
                        <p className="text-sm text-dark-muted max-w-[240px] mx-auto leading-relaxed">
                            Create products by linking strains with your favorite brands.
                        </p>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 gap-4">
                        {products.map((product) => (
                            <div key={product.id} className="card group hover:border-accent-purple/30 transition-all relative overflow-hidden">
                                <div className="absolute top-0 right-0 p-4 opacity-[0.03] pointer-events-none group-hover:opacity-10 transition-opacity">
                                    <Package size={120} />
                                </div>

                                <div className="flex items-start justify-between mb-6 relative z-10">
                                    <div className="flex items-center gap-4">
                                        <div className="p-3 rounded-2xl bg-dark-bg border border-dark-border group-hover:border-accent-purple/30 transition-all">
                                            {typeIcons[product.product_type]}
                                        </div>
                                        <div>
                                            <h3 className="font-black text-lg tracking-tight group-hover:text-white transition-colors">
                                                {product.label_name || product.strain?.name || 'Unknown Product'}
                                            </h3>
                                            <div className="flex items-center gap-2 mt-0.5">
                                                <span className="text-[8px] font-black uppercase tracking-widest px-1.5 py-0.5 rounded border border-accent-purple/20 bg-accent-purple/5 text-accent-purple">
                                                    {product.product_type}
                                                </span>
                                                <span className="text-[9px] text-dark-muted font-bold flex items-center gap-1 uppercase tracking-tighter">
                                                    <Tag size={10} className="text-accent-purple/40" /> {product.brand?.name || 'Unknown Brand'}
                                                </span>
                                                <span className="text-[9px] text-dark-muted font-bold flex items-center gap-1 uppercase tracking-tighter">
                                                    <Dna size={10} className="text-accent-purple/40" /> {product.strain?.name || 'Unknown Strain'}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="grid grid-cols-3 gap-3 mb-6 relative z-10">
                                    <div className="bg-dark-bg border border-dark-border/50 rounded-2xl p-3 text-center group-hover:border-accent-purple/20 transition-all">
                                        <p className="text-[8px] font-black text-dark-muted uppercase tracking-widest mb-1">Potency</p>
                                        <p className="text-sm font-black text-accent-green">{product.thc_percent ? `${product.thc_percent}%` : 'N/A'}</p>
                                    </div>
                                    <div className="bg-dark-bg border border-dark-border/50 rounded-2xl p-3 text-center group-hover:border-accent-purple/20 transition-all">
                                        <p className="text-[8px] font-black text-dark-muted uppercase tracking-widest mb-1">CBD</p>
                                        <p className="text-sm font-black text-accent-blue">{product.cbd_percent ? `${product.cbd_percent}%` : 'N/A'}</p>
                                    </div>
                                    <div className="bg-dark-bg border border-dark-border/50 rounded-2xl p-3 text-center group-hover:border-accent-purple/20 transition-all">
                                        <p className="text-[8px] font-black text-dark-muted uppercase tracking-widest mb-1">Genetic</p>
                                        <p className="text-sm font-black uppercase">{product.strain?.strain_type || 'N/A'}</p>
                                    </div>
                                </div>

                                <div className="flex gap-2 pt-4 border-t border-dark-border/50 relative z-10">
                                    <button
                                        onClick={() => handleEdit(product)}
                                        className="h-10 px-4 flex-1 text-[10px] font-black uppercase tracking-widest bg-dark-bg border border-dark-border rounded-xl hover:bg-accent-purple/10 hover:border-accent-purple/30 text-dark-muted hover:text-accent-purple transition-all flex items-center justify-center gap-2"
                                    >
                                        <Edit3 size={12} /> Edit
                                    </button>
                                    <button
                                        onClick={() => handleDelete(product.id)}
                                        className="h-10 px-4 flex-1 text-[10px] font-black uppercase tracking-widest bg-dark-bg border border-dark-border rounded-xl hover:bg-accent-red/10 hover:border-accent-red/30 text-dark-muted hover:text-accent-red transition-all flex items-center justify-center gap-2"
                                    >
                                        <Trash2 size={12} /> Remove
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                <div className="card bg-dark-surface/30 border-dark-border/40 border-dashed">
                    <div className="flex gap-4">
                        <div className="shrink-0 p-2 bg-dark-bg rounded-lg border border-dark-border self-start">
                            <Info size={16} className="text-accent-purple/60" />
                        </div>
                        <div>
                            <p className="text-xs font-black text-dark-muted uppercase tracking-widest mb-1">Registry Guide</p>
                            <p className="text-[11px] text-dark-muted/60 leading-relaxed font-medium">
                                Associations link a specific Brand's version of a Strain. This allows you to track consistency and value across different vendors.
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </AppLayout>
    );
}
